// Mentions (Phase 3 Module 20 Slice B) — inline-token parser + helpers.
//
// Format: `@[Full Name](pid:<uuid>)`
//
// Why this shape:
//   * Single contiguous token — mobile users can backspace the entire
//     mention in one keystroke.
//   * Parser is regex-local; no DB schema / column changes required.
//   * Encodes BOTH the display name (what the bubble shows) AND the
//     profile id (what notifications route on) so the renderer doesn't
//     need a second round-trip.
//
// The composer inserts the token verbatim when the user selects a
// suggestion — users see the raw `@[Amara Johnson](pid:…)` while editing,
// which is honest about what's going on and matches patterns from
// Linear/Gmail. Rendered bubbles show a clean pill.

// UUID canonical form. The pid group is uuid-only so an accidental `(pid:foo)`
// typed manually won't be picked up as a mention.
const UUID_RE = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

// Display name allowed chars — anything except ']' (the bracket terminator).
// Capped at 80 chars so a malformed token can't balloon into the entire body.
const NAME_RE = "[^\\]\\n]{1,80}";

export const MENTION_TOKEN_REGEX =
  new RegExp(`@\\[(${NAME_RE})\\]\\(pid:(${UUID_RE})\\)`, "g");

/**
 * Splits a message body into an ordered array of segments. Each is
 * either { type: 'text', text } or { type: 'mention', name, profileId }.
 * Preserves whitespace exactly so pre-wrap rendering stays intact.
 */
export function parseMentions(body) {
  if (!body || typeof body !== "string") return [];
  const out = [];
  let cursor = 0;
  // Clone the regex so concurrent calls don't share lastIndex.
  const re = new RegExp(MENTION_TOKEN_REGEX.source, "g");
  let m;
  while ((m = re.exec(body)) !== null) {
    if (m.index > cursor) {
      out.push({ type: "text", text: body.slice(cursor, m.index) });
    }
    out.push({ type: "mention", name: m[1], profileId: m[2] });
    cursor = m.index + m[0].length;
  }
  if (cursor < body.length) {
    out.push({ type: "text", text: body.slice(cursor) });
  }
  return out;
}

/**
 * Build a mention token for insertion into the composer. The caller is
 * responsible for trimming the partial `@query` the user typed before
 * pasting this in.
 */
export function buildMentionToken({ profileId, name }) {
  if (!profileId || !name) return "";
  // Strip any characters that would break the parser (just `]`).
  const safeName = String(name).replace(/\]/g, "").slice(0, 80);
  return `@[${safeName}](pid:${profileId})`;
}

/**
 * Pulls every mentioned profile id out of a message body. De-duplicated,
 * order-preserving. Used by the Thread send path to route notifications
 * with higher priority to mentioned recipients.
 */
export function extractMentionedProfileIds(body) {
  const ids = new Set();
  const seen = [];
  for (const seg of parseMentions(body)) {
    if (seg.type === "mention" && !ids.has(seg.profileId)) {
      ids.add(seg.profileId);
      seen.push(seg.profileId);
    }
  }
  return seen;
}

/**
 * Returns { trigger, query, start } describing an ACTIVE @mention typeahead
 * at the current caret, or null if the caret is not inside one.
 *
 * "Active" means: there's an `@` to the left of the caret, no whitespace
 * between it and the caret, and the @ is either at the start of the
 * body or preceded by whitespace/punctuation (so an email like
 * amara@daycare.test doesn't trigger the menu).
 */
export function detectMentionTrigger(body, caret) {
  if (!body || caret == null) return null;
  if (caret < 1) return null;

  // Walk backwards to find the nearest `@` that qualifies.
  let i = caret - 1;
  while (i >= 0) {
    const ch = body[i];
    if (ch === "@") {
      // Preceding char must be absent/whitespace/punctuation.
      const prev = i > 0 ? body[i - 1] : "";
      if (prev === "" || /[\s(,.:;!?]/.test(prev)) {
        const query = body.slice(i + 1, caret);
        // No whitespace allowed inside the query — that'd mean the user
        // moved past the mention attempt.
        if (/\s/.test(query)) return null;
        // Cap query length — keeps the dropdown filter cheap.
        if (query.length > 40) return null;
        return { trigger: "@", query, start: i };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
}

/**
 * Filter a participant directory by a typeahead query. Case-insensitive
 * substring match over full_name and email local part. Excludes the
 * current user (no @self mentions).
 */
export function filterMentionCandidates(candidates, query, selfId) {
  if (!Array.isArray(candidates)) return [];
  const needle = (query || "").toLowerCase().trim();
  const rows = candidates.filter((c) => c.profile_id !== selfId && (c.full_name || c.email));
  if (!needle) return rows.slice(0, 8);
  return rows
    .filter((c) => {
      const name = (c.full_name || "").toLowerCase();
      const local = (c.email || "").toLowerCase().split("@")[0];
      return name.includes(needle) || local.includes(needle);
    })
    .slice(0, 8);
}
